import React from 'react';
import GenericMetricsTable, { TableFilter } from '../GenericMetricsTable/GenericMetricsTable';
import { formatters } from '../GenericMetricsTable/utils';
import { TABLE_COLUMNS, TABLE_FILTERS, getTableHeaders } from './constants';
import { AbTestSegmentsMetricsTableProps, SegmentMetrics } from './AbTestSegmentsMetricsTable.types';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import TableCell from '../TableCell/TableCell';
import { Checkbox } from '@app/react-common/components/checkbox/Checkbox';
import { getIsThemeTest } from '../../../../../utils';
import { AbTestTypeCategory } from '../../../../../models/AbTestCreationType';
import { getTooltipText } from '../GenericMetricsTable/tooltips';

const renderHeader = (showCheckbox?: boolean, testType?: AbTestTypeCategory) => {
  const isThemeTest = testType ? getIsThemeTest(testType) : false;
  const tableHeaders = getTableHeaders(isThemeTest);

  return (
    <>
      {tableHeaders.map((header) => {
        const tooltipText = getTooltipText(header, isThemeTest);

        return (
          <div className="generic-metrics-table__head-cell" key={header}>
            {header}
            {tooltipText && (
              <Tooltip tooltipText={tooltipText}>
                <i className="icon icon-more_information" />
              </Tooltip>
            )}
          </div>
        );
      })}
    </>
  );
};

export default function AbTestSegmentsMetricsTable({
  title = 'Segments',
  segments,
  className = '',
  isLoading,
  showCheckbox = false,
  selectedSegmentIds = [],
  onSelectionChange,
  testType,
}: AbTestSegmentsMetricsTableProps) {
  const filters: TableFilter[] = TABLE_FILTERS.map(({ name, filter }) => ({
    key: name,
    label: name,
    filter,
  }));

  const [selectedIds, setSelectedIds] = React.useState<string[]>(selectedSegmentIds);

  const toggleSelection = (segment: SegmentMetrics, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...selectedIds, segment.segmentId]))
      : selectedIds.filter((id) => id !== segment.segmentId);
    setSelectedIds(next);
    if (onSelectionChange) {
      const selected = segments.filter((s) => next.includes(s.segmentId));
      onSelectionChange(selected);
    }
  };

  const renderRow = (segment: SegmentMetrics) => (
    <>
      {TABLE_COLUMNS.map(({ key, type }) => {
        const value = (formatters as any)[type](segment[key]);

        if (key === 'segmentName' && showCheckbox) {
          return (
            <TableCell key={key} isLoading={isLoading}>
              <div className="abtest-segments-metrics-table__name-with-checkbox">
                <Checkbox
                  checked={selectedIds.includes(segment.segmentId)}
                  disabled={false}
                  onChange={(checked) => toggleSelection(segment, checked)}
                />
                <div className="generic-metrics-table-cell__ellipsis">{value}</div>
              </div>
            </TableCell>
          );
        }

        return (
          <TableCell
            key={key}
            value={value || ''}
            className={type === 'currency' && value ? formatters.getCurrencyColorClass(segment[key]) : ''}
            ellipsis={key === 'segmentName'}
            isLoading={isLoading}
          />
        );
      })}
    </>
  );

  return (
    <GenericMetricsTable
      title={title}
      data={segments}
      filters={filters}
      className={`abtest-segments-metrics-table ${className}`}
      renderHeader={() => renderHeader(showCheckbox, testType)}
      renderRow={renderRow}
      getRowClassName={(segment) =>
        showCheckbox && selectedIds.includes((segment as SegmentMetrics).segmentId)
          ? 'generic-metrics-table__row--selected'
          : ''
      }
      keyExtractor={(segment, index) => `${segment.segmentName}-${index}`}
      isLoading={isLoading}
    />
  );
}
